import React, { useState, useRef, useEffect, InputHTMLAttributes } from 'react';
import './FloatingLabelInput.css';

interface FloatingLabelInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  isOptional?: boolean;
  /** When true, only allow letters (Unicode), diacritics, spaces, apostrophes, and dashes. */
  isName?: boolean;
  /** Max length to keep when filtering (applies when isName is true). */
  limit?: number;
}

const FloatingLabelInput: React.FC<FloatingLabelInputProps> = ({
  label,
  isOptional = false,
  isName = false,
  limit,
  value,
  onChange,
  onFocus,
  onBlur,
  className = '',
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Internal value for uncontrolled OR to reflect filtered value
  const [internalValue, setInternalValue] = useState<string>(
    typeof value === 'string' ? value : (value as any) ?? ''
  );
  const [isFocused, setIsFocused] = useState(false);

  // Keep internal in sync if parent controls `value`
  useEffect(() => {
    if (typeof value === 'string' && value !== internalValue) {
      setInternalValue(value);
    }
  }, [value, internalValue]);

  // Unicode-aware filters (only used when isName is true)
  // Allow: letters, combining marks (diacritics), spaces, apostrophes, dashes
  const STRIP_RE = /[^\p{L}\p{M}\s'-]/gu;
  const VALID_RE = /^[\p{L}\p{M}\s'-]+$/u;

  const applyFilters = (s: string): string => {
    if (!isName) return s;
    let out = s.replace(STRIP_RE, '');
    if (typeof limit === 'number' && limit > 0 && out.length > limit) {
      out = out.slice(0, limit);
    }
    return out;
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const filtered = applyFilters(raw);

    // Update internal value to the filtered version
    setInternalValue(filtered);

    // Keep native validity helpful for forms
    if (isName) {
      const ok = filtered.length === 0 ? !(rest.required ?? false) : VALID_RE.test(filtered);
      e.target.setCustomValidity(ok ? '' : 'Invalid name.');
    }

    // If parent provided onChange, forward an event whose value reflects the filtered text
    if (onChange) {
      // Create a shallow-cloned event with adjusted value fields
      const cloned = {
        ...e,
        target: { ...e.target, value: filtered },
        currentTarget: { ...e.currentTarget, value: filtered },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(cloned);
    }
  };

  const active = isFocused || !!internalValue;

  return (
    <div className={`floating-label-input ${className}`}>
      <input
        ref={inputRef}
        value={internalValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={`floating-label-input__field ${active ? 'active' : ''}`}
        // For non-JS/native fallback, we can hint with a simple pattern when isName is true.
        // Note: HTML pattern can't use Unicode \p{} escapes, so this is a lenient fallback.
        // {...(isName ? { pattern: "^[\\p{L}\\s'\\-]+$", flags: "u" } : {})}
        {...rest}
      />
      <label
        className={`floating-label-input__label ${active ? 'active' : ''}`}
        onClick={() => inputRef.current?.focus()}
      >
        <span className="floating-label-input__label-text">{label}</span>
      </label>
      {isOptional && (
        <span className="floating-label-input__optional">(optional)</span>
      )}
    </div>
  );
};

export default FloatingLabelInput;
